/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useState, useCallback } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Price } from '@adobe-commerce/elsie/components';

import '@/pdp/components/DownloadableOptions/DownloadableOptions.css';

export type DownloadableLink = {
  uid: string;
  price: number;
  sample_url: string | null;
  label: string;
  number_of_downloads: number;
};

export type DownloadableSample = {
  uid: string;
  label: string;
  url: string;
};

export type DownloadableValue = {
  links: DownloadableLink[];
  samples: DownloadableSample[];
  purchaseSeparately: boolean;
};

export interface DownloadableOptionsProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'value'> {
  label: string;
  value: DownloadableValue;
  onLinkChange?: (uid: string, selected: boolean) => void;
  currency?: string;
  locale?: string;
}

export const DownloadableOptions: FunctionComponent<
  DownloadableOptionsProps
> = ({ label, value, onLinkChange, currency, locale, className, ...props }) => {
  const SampleLabel = useText('PDP.DownloadableOptions.Sample.label').label;
  const ViewSampleLabel = useText('PDP.DownloadableOptions.ViewSample.label');

  const { links, samples, purchaseSeparately } = value;

  const initialSelected = purchaseSeparately
    ? new Set<string>()
    : new Set(links.map((link) => link.uid));

  const [selected, setSelected] = useState<Set<string>>(initialSelected);

  const handleToggle = useCallback(
    (uid: string, isSelected: boolean, isDisabled: boolean) => {
      if (!purchaseSeparately || isDisabled) {
        return;
      }

      setSelected((prev) => {
        const next = new Set(prev);
        if (isSelected) {
          next.add(uid);
        } else {
          next.delete(uid);
        }
        return next;
      });
      onLinkChange?.(uid, isSelected);
    },
    [onLinkChange, purchaseSeparately]
  );

  const renderLink = (link: DownloadableLink) => {
    const isSelected = purchaseSeparately && selected.has(link.uid);

    return (
      <div
        key={link.uid}
        className={`pdp-downloadable-options__item ${
          isSelected ? 'pdp-downloadable-options__item--selected' : ''
        } ${
          !purchaseSeparately ? 'pdp-downloadable-options__item--disabled' : ''
        }`}
      >
        <button
          type="button"
          className="pdp-downloadable-options__clickable"
          aria-pressed={isSelected}
          disabled={!purchaseSeparately}
          onClick={() =>
            purchaseSeparately &&
            handleToggle(link.uid, !isSelected, !purchaseSeparately)
          }
          onKeyDown={(e) => {
            if (purchaseSeparately && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleToggle(link.uid, !isSelected, !purchaseSeparately);
            }
          }}
        >
          <div className="pdp-downloadable-options__content">
            <div className="pdp-downloadable-options__header">
              <span className="pdp-downloadable-options__title">
                {link.label}
              </span>
              <Price
                amount={link.price}
                currency={currency}
                locale={locale}
                className="pdp-downloadable-options__price"
              />
            </div>
          </div>
        </button>
        {link.sample_url && (
          <a
            href={link.sample_url}
            className="pdp-downloadable-options__sample-button"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ViewSampleLabel.label.replace('{label}', link.label)}
          >
            {SampleLabel}
          </a>
        )}
      </div>
    );
  };

  const renderSample = (sample: DownloadableSample) => {
    return (
      <div
        key={sample.uid}
        className="pdp-downloadable-options__item pdp-downloadable-options__item--sample"
      >
        <button
          type="button"
          className="pdp-downloadable-options__clickable"
          aria-pressed={false}
          disabled={true}
        >
          <div className="pdp-downloadable-options__content">
            <div className="pdp-downloadable-options__header">
              <span className="pdp-downloadable-options__title">
                {sample.label}
              </span>
            </div>
          </div>
        </button>
        <a
          href={sample.url}
          className="pdp-downloadable-options__sample-button"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${SampleLabel} ${sample.label}`}
        >
          {SampleLabel}
        </a>
      </div>
    );
  };

  return (
    <div
      {...props}
      className={classes(['pdp-downloadable-options', className])}
      data-slot-key="product-downloadable-options"
    >
      <div className="pdp-downloadable-options__field">
        <div className="pdp-downloadable-options__field__label">{label}</div>

        <div className="pdp-downloadable-options__list">
          {links.map((link) => renderLink(link))}
          {samples.map((sample) => renderSample(sample))}
        </div>
      </div>
    </div>
  );
};
